import type { UseField } from '@seek/forms-ui/lib/Field/useField';
import type { Language } from '@seek/forms-ui/lib/types';
import { required, isEmail } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { TextField, useResponsiveValue } from 'braid-design-system';
import type React from 'react';

import { useAppContext } from '../../../../../../components/AppContext';
import useContextEmail from '../../../../../../components/hooks/useContextEmail';
import type { FormName } from '../../../../../../types';
import { useLoginPageContext } from '../../../LoginPageContext';

import translations from './.vocab';

interface Props<FormValues> {
  useField: UseField<FormValues, Language>;
  formName: FormName;
}

function EmailField<FormValues>({ useField, formName }: Props<FormValues>) {
  const responsiveValue = useResponsiveValue();
  const getId = () => {
    switch (formName) {
      case 'register':
        return 'newEmailAddress';
      default:
        return 'emailAddress';
    }
  };

  const emailAddress = useField({
    id: getId() as keyof FormValues,
    validators: [required, isEmail],
  });
  const emailValue = emailAddress.value as string;

  const { tenant } = useAppContext();
  const { setEmailAddress } = useLoginPageContext();

  useContextEmail(emailValue, setEmailAddress);
  const { t } = useTranslations(translations);

  const getTone = () => {
    if (!emailAddress.valid) {
      return 'critical';
    }
    return 'neutral';
  };

  const getMessage = () => {
    if (!emailAddress.valid) {
      return <span>{emailAddress.errorMessage}</span>;
    }
  };

  const getDescription = () => {
    const size = responsiveValue({
      mobile: 'Small',
      desktop: 'Large',
    });
    if (size === 'Small') return '';

    switch (formName) {
      case 'register':
        return '';
      case 'email_lookup':
        return t('Please enter your email address to begin.');
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    emailAddress.onChange(e);
    const newValue = e.target.value;
    setEmailAddress(newValue);
  };

  return (
    <TextField
      id={getId()}
      name={`${getId()}_${tenant}`}
      type="email"
      autoComplete={`${tenant} username`}
      tone={getTone()}
      description={getDescription()}
      message={getMessage()}
      autoFocus
      label={t('Email address')}
      value={emailValue}
      reserveMessageSpace={true}
      onChange={onChange}
      onBlur={emailAddress.onBlur}
    />
  );
}

export default EmailField;
